import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import type { ConsignmentReconciliationResponse } from '../types/venue';
import {
  buildSupplierStatementData,
  SUPPLIER_STATEMENT_NOTE,
  type SupplierStatementData,
} from './consignmentSupplierStatement';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const fmtCost = (v: number | null): string =>
  v != null ? v.toFixed(2) : '—';

const safeName = (s: string): string =>
  s.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 30);

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    fontFamily:       'Helvetica',
    fontSize:         9,
    color:            '#1a1a1a',
    paddingTop:       44,
    paddingBottom:    56,
    paddingHorizontal: 40,
    lineHeight:       1.4,
  },

  header: {
    flexDirection:  'row',
    justifyContent: 'space-between',
    alignItems:     'flex-start',
    marginBottom:   20,
  },
  venueName:    { fontFamily: 'Helvetica-Bold', fontSize: 14, color: '#111', marginBottom: 2 },
  venueSubtitle: { fontSize: 7.5, color: '#999', letterSpacing: 0.8 },
  docBlock:     { alignItems: 'flex-end' },
  docTitle:     { fontFamily: 'Helvetica-Bold', fontSize: 18, color: '#111', letterSpacing: 1.5 },
  docSub:       { fontSize: 7.5, color: '#666', marginTop: 2 },
  stmtNumber:   { fontFamily: 'Helvetica-Bold', fontSize: 8, color: '#444', marginTop: 6, letterSpacing: 0.5 },

  ruleBold:  { borderBottomWidth: 2, borderBottomColor: '#111', borderBottomStyle: 'solid', marginBottom: 14 },
  ruleLight: { borderBottomWidth: 1, borderBottomColor: '#e0e0e0', borderBottomStyle: 'solid', marginBottom: 14 },

  metaRow:   { flexDirection: 'row', marginBottom: 14, flexWrap: 'wrap' },
  metaItem:  { marginRight: 28, marginBottom: 4 },
  metaLabel: { fontSize: 6.5, color: '#999', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 3 },
  metaValue: { fontFamily: 'Helvetica-Bold', fontSize: 9, color: '#111' },

  sectionHead: {
    fontFamily: 'Helvetica-Bold',
    fontSize:   7,
    color:      '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    marginBottom: 6,
  },

  table:     { marginBottom: 14 },
  tableHead: {
    flexDirection:    'row',
    backgroundColor:  '#111',
    paddingVertical:  6,
    paddingHorizontal: 6,
  },
  thCell:    { fontFamily: 'Helvetica-Bold', fontSize: 6, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow:  {
    flexDirection:    'row',
    paddingVertical:  6,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
    borderBottomStyle: 'solid',
  },
  tableRowAlt:    { backgroundColor: '#f7f7f7' },
  totalsRow:      {
    flexDirection:    'row',
    paddingVertical:  7,
    paddingHorizontal: 6,
    backgroundColor:  '#111',
  },
  tdText:  { fontSize: 7.5, color: '#222' },
  tdMuted: { fontSize: 7.5, color: '#777' },
  tdBold:  { fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#222' },
  tdWhite: { fontFamily: 'Helvetica-Bold', fontSize: 7.5, color: '#fff' },

  // Statement table column widths
  colItem:    { flex: 3.5 },
  colQty:     { flex: 1,   textAlign: 'right' },
  colCost:    { flex: 1.2, textAlign: 'right' },
  colAmount:  { flex: 1.5, textAlign: 'right' },
  colLabel:   { flex: 3.5 + 1 + 1 + 1 }, // spans item + opening + received + closing

  noteBox: {
    borderLeftWidth:  2,
    borderLeftColor:  '#ccc',
    borderLeftStyle:  'solid',
    paddingLeft:      8,
    paddingVertical:  5,
    marginBottom:     14,
  },
  noteText: { fontSize: 7.5, color: '#555', lineHeight: 1.5 },

  footer: {
    position:   'absolute',
    bottom:     20,
    left:       40,
    right:      40,
    flexDirection:    'row',
    justifyContent:   'space-between',
    borderTopWidth:   1,
    borderTopColor:   '#e8e8e8',
    borderTopStyle:   'solid',
    paddingTop:       6,
  },
  footerLeft:  { fontSize: 6, color: '#bbb' },
  footerRight: { fontSize: 6, color: '#bbb', textAlign: 'right' },
});

// ── Document component ────────────────────────────────────────────────────────

interface Props { data: SupplierStatementData; venueName: string }

const SupplierStatementDocument: React.FC<Props> = ({ data, venueName }) => (
  <Document
    title={`Supplier Statement — ${data.accountName}`}
    author={venueName}
  >
    <Page size="A4" orientation="portrait" style={s.page} wrap>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <View style={s.header}>
        <View>
          <Text style={s.venueName}>{venueName}</Text>
          <Text style={s.venueSubtitle}>Consignment Statement</Text>
        </View>
        <View style={s.docBlock}>
          <Text style={s.docTitle}>SUPPLIER</Text>
          <Text style={s.docTitle}>STATEMENT</Text>
          <Text style={s.docSub}>Consignment consumption summary</Text>
          <Text style={s.stmtNumber}>{data.statementNumber}</Text>
        </View>
      </View>

      <View style={s.ruleBold} />

      {/* ── Metadata ────────────────────────────────────────────────── */}
      <View style={s.metaRow}>
        {data.supplierName ? (
          <View style={s.metaItem}>
            <Text style={s.metaLabel}>Consignor</Text>
            <Text style={s.metaValue}>{data.supplierName}</Text>
          </View>
        ) : null}
        <View style={s.metaItem}>
          <Text style={s.metaLabel}>Account</Text>
          <Text style={s.metaValue}>{data.accountName}</Text>
        </View>
        <View style={s.metaItem}>
          <Text style={s.metaLabel}>Period From</Text>
          <Text style={s.metaValue}>{fmtDate(data.periodFrom)}</Text>
        </View>
        <View style={s.metaItem}>
          <Text style={s.metaLabel}>Period To</Text>
          <Text style={s.metaValue}>{fmtDate(data.periodTo)}</Text>
        </View>
        <View style={s.metaItem}>
          <Text style={s.metaLabel}>Generated</Text>
          <Text style={s.metaValue}>{fmtDateTime(data.generatedAt)}</Text>
        </View>
      </View>

      <View style={s.ruleLight} />

      {/* ── Statement table ──────────────────────────────────────────── */}
      <Text style={s.sectionHead}>Inventory Consumption</Text>
      <View style={s.table}>

        {/* Column headers */}
        <View style={s.tableHead} fixed>
          <Text style={[s.thCell, s.colItem]}>Item</Text>
          <Text style={[s.thCell, s.colQty]}>Opening</Text>
          <Text style={[s.thCell, s.colQty]}>Received</Text>
          <Text style={[s.thCell, s.colQty]}>Closing</Text>
          <Text style={[s.thCell, s.colQty]}>Consumed</Text>
          <Text style={[s.thCell, s.colCost]}>Unit Cost</Text>
          <Text style={[s.thCell, s.colAmount]}>Amount</Text>
        </View>

        {data.rows.length === 0 ? (
          <View style={s.tableRow}>
            <Text style={[s.tdMuted, { flex: 1, textAlign: 'center' }]}>No stock activity in this period.</Text>
          </View>
        ) : (
          data.rows.map((row, idx) => (
            <View
              key={`${row.displayName}-${idx}`}
              style={[s.tableRow, idx % 2 === 1 ? s.tableRowAlt : {}]}
              wrap={false}
            >
              <Text style={[s.tdText, s.colItem]}>{row.displayName}</Text>
              <Text style={[s.tdMuted, s.colQty]}>{row.openingQty}</Text>
              <Text style={[s.tdMuted, s.colQty]}>{row.receivedQty > 0 ? `+${row.receivedQty}` : '—'}</Text>
              <Text style={[s.tdMuted, s.colQty]}>{row.closingQty}</Text>
              <Text style={[s.tdBold, s.colQty]}>{row.consumedQty > 0 ? row.consumedQty : '—'}</Text>
              <Text style={[s.tdMuted, s.colCost]}>{fmtCost(row.unitCost)}</Text>
              <Text style={[row.amount != null ? s.tdBold : s.tdMuted, s.colAmount]}>
                {row.amount != null ? row.amount.toFixed(2) : '—'}
              </Text>
            </View>
          ))
        )}

        {/* Totals row */}
        <View style={s.totalsRow} wrap={false}>
          <Text style={[s.tdWhite, s.colLabel]}>Total</Text>
          <Text style={[s.tdWhite, s.colQty]}>{data.totalConsumed}</Text>
          <Text style={[s.tdWhite, s.colCost]}>{''}</Text>
          <Text style={[s.tdWhite, s.colAmount]}>
            {data.totalAmount != null ? data.totalAmount.toFixed(2) : '—'}
          </Text>
        </View>
      </View>

      <View style={s.ruleLight} />

      {/* ── Note ────────────────────────────────────────────────────── */}
      <View style={s.noteBox}>
        <Text style={s.noteText}>{SUPPLIER_STATEMENT_NOTE}</Text>
      </View>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <View style={s.footer} fixed>
        <Text style={s.footerLeft}>
          {data.statementNumber} · {data.accountName}
        </Text>
        <Text
          style={s.footerRight}
          render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />
      </View>

    </Page>
  </Document>
);

// ── Download helper ───────────────────────────────────────────────────────────

export async function downloadConsignmentSupplierPdf(
  report: ConsignmentReconciliationResponse,
  venueName: string,
): Promise<void> {
  const data = buildSupplierStatementData(report);
  const doc  = <SupplierStatementDocument data={data} venueName={venueName} />;
  const blob = await pdf(doc).toBlob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `SupplierStatement-${safeName(data.accountName)}-${data.periodTo.slice(0, 10)}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
